import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { GameCreateRequest, GameImportDto, GameUpdateRequest } from '../../api/types';
import { canManageGames } from '../../domain/games/policy';
import { GameTagInput } from '../../features/games/GameTagInput';
import { ImportGameModal } from '../../features/games/ImportGameModal';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectGameById } from '../../store/selectors/gamesSelectors';
import { createGame, fetchGameById, updateGame } from '../../store/thunks/gamesThunks';

import './GameFormPage.css';

export function GameFormPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const isEditing = Boolean(gameId);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const existingGame = useAppSelector(selectGameById(gameId ?? ''));
  const role = useAppSelector((s) => s.auth.user?.role);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [gameUrl, setGameUrl] = useState('');
  const [minPlayers, setMinPlayers] = useState(2);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [estimatedDuration, setEstimatedDuration] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [submitting, setSubmitting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const handleImported = (data: GameImportDto) => {
    if (data.name) setName(data.name);
    if (data.description) setDescription(data.description);
    if (data.imageUrl) setImageUrl(data.imageUrl);
    if (data.gameUrl) setGameUrl(data.gameUrl);
    if (data.minPlayers != null) setMinPlayers(data.minPlayers);
    if (data.maxPlayers != null) setMaxPlayers(data.maxPlayers);
    if (data.estimatedDuration != null) setEstimatedDuration(String(data.estimatedDuration));
    if (data.tags?.length) setTags(data.tags);
  };

  useEffect(() => {
    if (!role) return;
    if (!canManageGames(role)) {
      navigate(-1);
    }
  }, [role, navigate]);

  useEffect(() => {
    if (isEditing && gameId) {
      dispatch(fetchGameById({ gameId }));
    }
  }, [dispatch, isEditing, gameId]);

  useEffect(() => {
    if (existingGame && isEditing) {
      setName(existingGame.name);
      setDescription(existingGame.description ?? '');
      setImageUrl(existingGame.imageUrl ?? '');
      setGameUrl(existingGame.gameUrl ?? '');
      setMinPlayers(existingGame.minPlayers);
      setMaxPlayers(existingGame.maxPlayers);
      setEstimatedDuration(existingGame.estimatedDuration != null ? String(existingGame.estimatedDuration) : '');
      setTags(existingGame.tags.map((t) => t.name));
    }
  }, [existingGame, isEditing]);

  const validate = (): boolean => {
    const newErrors: Record<string, string | null> = {};
    if (!name.trim()) newErrors.name = 'Название обязательно';
    else if (name.trim().length > 100) newErrors.name = 'Максимум 100 символов';
    if (minPlayers < 1) newErrors.minPlayers = 'Минимум 1 игрок';
    if (maxPlayers < minPlayers) newErrors.maxPlayers = 'Должно быть ≥ мин. игроков';
    setErrors(newErrors);
    return Object.values(newErrors).every((v) => v == null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      if (isEditing && gameId) {
        const payload: GameUpdateRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          imageUrl: imageUrl.trim() || undefined,
          gameUrl: gameUrl.trim() || undefined,
          minPlayers,
          maxPlayers,
          estimatedDuration: estimatedDuration ? Number(estimatedDuration) : undefined,
          tags,
        };
        await dispatch(updateGame({ gameId, payload })).unwrap();
        navigate(`/games/${gameId}`);
      } else {
        const payload: GameCreateRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          imageUrl: imageUrl.trim() || undefined,
          gameUrl: gameUrl.trim() || undefined,
          minPlayers,
          maxPlayers,
          estimatedDuration: estimatedDuration ? Number(estimatedDuration) : undefined,
          tags,
        };
        const result = await dispatch(createGame(payload)).unwrap();
        navigate(`/games/${result.id}`);
      }
    } catch {
      setErrors({ submit: 'Не удалось сохранить' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="game-form-page ttbFormContainer">
      <div className="ttbFormHeader">
        <button className="ttbButtonOutlineHeader" onClick={() => navigate(-1)}>
          ← Назад
        </button>
        <h2 className="ttbFormHeaderTitle">
          {isEditing ? 'Редактировать игру' : 'Новая игра'}
        </h2>
        {!isEditing && (
          <button
            type="button"
            className="ttbButtonOutlineHeader game-form__import-btn"
            onClick={() => setImportModalOpen(true)}
          >
            Импорт
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">🎲</span>
            <span className="ttbFormSectionTitle">Основная информация</span>
          </div>

          <div className="ttbField">
            <label className="ttbFieldLabel ttbFieldLabel-required">Название</label>
            <input
              className={`ttbInput${errors.name ? ' ttbInputError' : ''}`}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Колонизаторы"
            />
            {errors.name && <span className="ttbFieldError">{errors.name}</span>}
          </div>

          <div className="ttbField">
            <label className="ttbFieldLabel">Описание</label>
            <textarea
              className="ttbInput game-form__textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Краткое описание игры..."
              rows={3}
            />
          </div>

          <div className="ttbField">
            <label className="ttbFieldLabel">URL обложки</label>
            <input
              className="ttbInput"
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
            />
            <span className="ttbFieldHint">Необязательно. Ссылка на изображение обложки игры</span>
          </div>

          <div className="ttbField">
            <label className="ttbFieldLabel">Ссылка на игру</label>
            <input
              className="ttbInput"
              type="url"
              value={gameUrl}
              onChange={(e) => setGameUrl(e.target.value)}
              placeholder="https://..."
            />
            <span className="ttbFieldHint">Необязательно. Ссылка на описание правил или страницу игры</span>
          </div>
        </div>

        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">👥</span>
            <span className="ttbFormSectionTitle">Количество игроков</span>
          </div>

          <div className="game-form__players-row">
            <div className="game-form__player-field">
              <label className="ttbFieldLabel ttbFieldLabel-required">Мин.</label>
              <input
                className={`ttbInput game-form__number-input${errors.minPlayers ? ' ttbInputError' : ''}`}
                type="number"
                min={1}
                value={minPlayers}
                onChange={(e) => setMinPlayers(Number(e.target.value))}
              />
              {errors.minPlayers && <span className="ttbFieldError">{errors.minPlayers}</span>}
            </div>
            <span className="game-form__players-separator">—</span>
            <div className="game-form__player-field">
              <label className="ttbFieldLabel ttbFieldLabel-required">Макс.</label>
              <input
                className={`ttbInput game-form__number-input${errors.maxPlayers ? ' ttbInputError' : ''}`}
                type="number"
                min={1}
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(Number(e.target.value))}
              />
              {errors.maxPlayers && <span className="ttbFieldError">{errors.maxPlayers}</span>}
            </div>
          </div>
        </div>

        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">⏱</span>
            <span className="ttbFormSectionTitle">Продолжительность</span>
          </div>

          <div className="ttbField">
            <label className="ttbFieldLabel">Примерное время игры (мин.)</label>
            <input
              className="ttbInput game-form__number-input"
              type="number"
              min={1}
              value={estimatedDuration}
              onChange={(e) => setEstimatedDuration(e.target.value)}
              placeholder="60"
            />
            <span className="ttbFieldHint">Необязательно. Среднее время одной партии в минутах</span>
          </div>
        </div>

        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">🏷️</span>
            <span className="ttbFormSectionTitle">Теги / категории</span>
          </div>
          <GameTagInput selectedTags={tags} onTagsChange={setTags} />
        </div>

        {errors.submit && (
          <div className="ttbFieldError" style={{ textAlign: 'center', marginBottom: 12 }}>
            {errors.submit}
          </div>
        )}

        <div className="ttbFormActions">
          <button
            className="ttbButtonSubmit"
            type="submit"
            disabled={submitting}
          >
            {submitting ? 'Сохранение...' : isEditing ? 'Сохранить' : 'Создать игру'}
          </button>
        </div>
      </form>

      <ImportGameModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={handleImported}
      />
    </div>
  );
}
